
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import SampleItineraryModal, { SampleVoyage } from './SampleItineraryModal';
import { trackEmailCapture, trackSampleView, trackDemoClick } from '../services/trackingService';
import { PRICING, COPY, fmt, foundingSeatsLabel } from '../config/pricing';
import ScrollToTop from './ScrollToTop';
import KobuNav from './kobu/KobuNav';
import KobuFooter from './kobu/KobuFooter';
import { KobuViewAll, KobuBackToTop } from './kobu/Kobu';
import { Shield, Zap, Globe, ArrowRight, ChevronLeft, ChevronRight, Mail, Check } from 'lucide-react';

// Preload AuthScreen chunk so login appears instantly when user clicks CTA
const preloadAuthScreen = () => import('./AuthScreen');

interface LandingPageProps {
  onEnter: () => void;
}

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800&q=75&fm=webp&auto=format&fit=crop", // African Safari (Elephants)
  "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=800&q=75&fm=webp&auto=format&fit=crop", // Maldives (Luxury Water Villa)
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=75&fm=webp&auto=format&fit=crop", // Paris (Eiffel Tower)
  "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800&q=75&fm=webp&auto=format&fit=crop", // San Francisco (Golden Gate)
  "https://images.unsplash.com/photo-1587595431973-160d0d94add1?w=800&q=75&fm=webp&auto=format&fit=crop", // Machu Picchu
];

const JOURNAL_ENTRIES: SampleVoyage[] = [
    {
        id: "nsb",
        title: "New Smyrna: Salt & Solitude",
        location: "Florida, USA",
        // Image: Atlantic coast dunes & surf — verified working Unsplash ID (also used elsewhere as TRAVEL_FALLBACK)
        image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=75&fm=webp&auto=format&fit=crop",
        description: "A curated retreat to the Atlantic's hidden artistic enclave. Where Brutalist architecture meets wild dunes, and the surf culture is steeped in quiet history.",
        tags: ["Coastal Americana", "Surf Noir"],
        days: [
            {
                day: 1,
                title: "The Golden Dune",
                morning: "Arrival at a private estate on the non-driving coast. The horizon is unbroken. The scent of sea grapes and salt fills the air.",
                afternoon: "A private consultation at a Flagler Avenue gallery. Acquiring local clay works. Espresso at a garden hideaway.",
                evening: "Dinner at The Garden Table. Farm-to-coast cuisine served under a canopy of ancient oaks and Spanish moss."
            },
            {
                day: 2,
                title: "Concrete & Canvas",
                morning: "Pilgrimage to the Atlantic Center for the Arts. Wandering the wooden boardwalks connecting the jungle-immersed modernist studios.",
                afternoon: "Private surf instruction at the Inlet. Riding the long peelers of the Atlantic. Lunch of fresh Canaveral shrimp.",
                evening: "Sunset sail on the Intracoastal. Watching the manatees drift in the amber light. Rustic riverfront seafood."
            },
            {
                day: 3,
                title: "The Inlet Drift",
                morning: "Sunrise yoga on the North Beach jetty. The sound of water crashing against coquina rock.",
                afternoon: "Kayaking the mosquito lagoon backwaters. Complete silence amongst the mangroves.",
                evening: "Farewell cocktails at a rooftop lounge overlooking the estuary. The sky turns to violet and fire."
            }
        ]
    },
    {
        id: "kyoto",
        title: "Kyoto After Dark",
        location: "Japan",
        // Moody Kyoto Alley
        image: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=75&fm=webp&auto=format&fit=crop",
        description: "An exploration of the shadow world of the ancient capital. From hidden whiskey bars in Pontocho to moonlight meditations in private Zen gardens.",
        tags: ["Cultural Immersion", "Vogue Minimal"],
        days: [
            {
                day: 1,
                title: "Shadows of Gion",
                morning: "Arrival at Aman Kyoto. The property is a secret garden hidden in the foothills. The air smells of moss and cedar.",
                afternoon: "Private tea ceremony with a 15th-generation master. The whisking of the matcha is the only sound in the room.",
                evening: "Walking tour of Gion with a Geisha scholar. Access to a private ochaya (tea house) that does not accept new customers."
            },
            {
                day: 2,
                title: "The Bamboo Silence",
                morning: "Arashiyama Bamboo Grove at sunrise, before the crowds. The light filters through the stalks like stained glass.",
                afternoon: "Lunch at Monk, a 7-seat restaurant overlooking the Philosopher's Path. Foraged vegetables roasted in a wood-fired oven.",
                evening: "Bar hopping in Pontocho. Tiny, 5-seat bars serving rare Japanese whiskeys. The atmosphere is smoky, intimate, and cinematic."
            },
            {
                day: 3,
                title: "Art & Antiquity",
                morning: "Visit to the Miho Museum. The approach through the tunnel is designed to feel like entering Shangri-La.",
                afternoon: "Shopping for antique ceramics on Teramachi Street. Finding a wabi-sabi bowl from the Edo period.",
                evening: "Kaiseki dinner at Kikunoi. Ten courses reflecting the micro-season. A final toast with sake from the Fushimi district."
            }
        ]
    },
    {
        id: "amalfi",
        title: "Amalfi Coast by Sea",
        location: "Italy",
        // Updated: More vibrant, picturesque Positano view with sea and colored buildings
        image: "https://images.unsplash.com/photo-1520175480921-4edfa2983e0f?w=800&q=75&fm=webp&auto=format&fit=crop",
        description: "La Dolce Vita reimagined. A sea-centric perspective of the coast, avoiding the roads entirely. Vintage wooden boats, lemon groves, and cliffside grandeur.",
        tags: ["Epicurean Discovery", "Golden Hour"],
        days: [
            {
                day: 1,
                title: "High Above the Sea",
                morning: "Helicopter arrival in Ravello. Checking into Belmond Hotel Caruso. The infinity pool seems to drop straight into the Mediterranean.",
                afternoon: "A slow lunch of lemon risotto in the hotel gardens. The scent of citrus blossoms is overwhelming.",
                evening: "Private concert in the gardens of Villa Rufolo. Wagner found inspiration here, and tonight, you understand why."
            },
            {
                day: 2,
                title: "The Blue Grotto",
                morning: "Boarding a vintage Riva Aquarama. Cruising to Capri. The mahogany wood gleams against the turquoise water.",
                afternoon: "Lunch at La Fontelina. Sea urchin pasta and crisp white wine. Swimming off the rocks in the shadow of the Faraglioni.",
                evening: "Sunset aperitivo in Positano at Le Sirenuse. The town lights up like a nativity scene as the sun dips below the horizon."
            },
            {
                day: 3,
                title: "Path of the Gods",
                morning: "Hiking the Sentiero degli Dei. The view stretches all the way to Praiano. Wild rosemary and thyme scent the trail.",
                afternoon: "Private boat to Conca dei Marini for lunch at a restaurant reachable only by sea.",
                evening: "Farewell dinner at Don Alfonso 1890. A culinary temple dedicated to the flavors of the Sorrento peninsula."
            }
        ]
    },
    {
        id: "orlando",
        title: "Orlando: The Grand Oasis",
        location: "Florida, USA",
        image: "https://images.unsplash.com/photo-1597466599360-3b9775841aec?w=800&q=75&fm=webp&auto=format&fit=crop",
        description: "Beyond the parks lies a world of lakeside elegance and refined gastronomy. A curated journey through Orlando's sophisticated gardens and private estates.",
        tags: ["Lakeside Luxury", "Tropical Modernism"],
        days: [
            {
                day: 1,
                title: "Lakeside Serenity",
                morning: "Arrival at a private villa overlooking Lake Eola. The morning mist clings to the swan-filled waters. Fresh citrus breakfast on the terrace.",
                afternoon: "A private tour of the Harry P. Leu Gardens. Wandering through 50 acres of tropical flora and ancient camellias.",
                evening: "Dinner at a Michelin-starred hideaway in Winter Park. Modern Floridian cuisine paired with rare vintages."
            },
            {
                day: 2,
                title: "The Garden District",
                morning: "Exploring the boutiques of Park Avenue. A private styling session at a high-end atelier.",
                afternoon: "Boat tour through the Winter Park chain of lakes. Viewing the historic estates and hidden canals from the water.",
                evening: "Sunset cocktails at a rooftop lounge with views of the skyline. The city lights reflect off the surrounding lakes."
            },
            {
                day: 3,
                title: "The Final Bloom",
                morning: "Sunrise paddleboarding on the clear waters of a spring-fed lake. The silence of the early morning is absolute.",
                afternoon: "A visit to the Morse Museum of American Art to view the world's most comprehensive collection of Tiffany glass.",
                evening: "Farewell gala at a private estate. A celebration of the Florida sun and the refined spirit of the City Beautiful."
            }
        ]
    }
];

const LandingPage: React.FC<LandingPageProps> = ({ onEnter }) => {
  const navigate = useNavigate();
  useSEO({
    title: 'Aura Traveler — Intelligent Luxury Travel Design',
    description:
      'Aura Traveler is an AI-powered luxury travel design platform. Generate magazine-quality travel itineraries for any destination in under 90 seconds. Built for agencies, hotels, and discerning travelers.',
    canonical: 'https://www.auratraveler.com/',
  });

  // Preload AuthScreen after landing page renders (so it's instant when user clicks)
  useEffect(() => {
    const timer = setTimeout(preloadAuthScreen, 1500);
    return () => clearTimeout(timer);
  }, []);

  const [scrollY, setScrollY] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSample, setSelectedSample] = useState<SampleVoyage | null>(null);

  // Carousel State
  const [journalIndex, setJournalIndex] = useState(0);

  // Dispatch Newsletter State
  const [dispatchEmail, setDispatchEmail] = useState('');
  const [dispatchStatus, setDispatchStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [dispatchMessage, setDispatchMessage] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleDispatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dispatchEmail.trim()) return;
    setDispatchStatus('sending');
    setDispatchMessage('');
    try {
      const { db } = await import('../services/firebase');
      const { collection, addDoc, query, where, getDocs, limit, serverTimestamp } = await import('firebase/firestore');
      if (!db) throw new Error('Firestore unavailable');
      const subscribersRef = collection(db, 'newsletter_subscribers');
      const existing = await getDocs(query(subscribersRef, where('email', '==', dispatchEmail.trim().toLowerCase()), limit(1)));
      if (!existing.empty) {
        setDispatchStatus('success');
        setDispatchMessage('already_subscribed');
        setDispatchEmail('');
        return;
      }
      await addDoc(subscribersRef, {
        email: dispatchEmail.trim().toLowerCase(),
        source: 'landing_page',
        subscribedAt: serverTimestamp(),
      });
      trackEmailCapture('landing');
      setDispatchStatus('success');
      setDispatchMessage('subscribed');
      setDispatchEmail('');
    } catch {
      setDispatchStatus('error');
      setDispatchMessage('Unable to subscribe right now. Please try again later.');
    }
  };

  // Only re-render when crossing the navbar background threshold (50px)
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const y = window.scrollY;
          setScrollY(prev => {
            const crossedUp = prev < 50 && y >= 50;
            const crossedDown = prev >= 50 && y < 50;
            return (crossedUp || crossedDown) ? y : prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Hero Image Rotation Logic
  useEffect(() => {
    const interval = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000); // 5 Seconds
    return () => clearInterval(interval);
  }, []);

  // Preload all hero images on mount so carousel transitions are instant
  useEffect(() => {
    HERO_IMAGES.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nextJournal = () => {
    setJournalIndex((prev) => (prev + 1) % JOURNAL_ENTRIES.length);
  };

  const prevJournal = () => {
    setJournalIndex((prev) => (prev - 1 + JOURNAL_ENTRIES.length) % JOURNAL_ENTRIES.length);
  };

  const activeJournal = JOURNAL_ENTRIES[journalIndex];

  return (
    <div className="min-h-screen bg-parchment text-obsidian font-gill selection:bg-obsidian/10 selection:text-obsidian overflow-x-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Aura Traveler",
        "url": "https://www.auratraveler.com",
        "description": "AI-powered luxury travel itinerary generator",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.auratraveler.com/?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Aura Traveler",
        "url": "https://www.auratraveler.com",
        "logo": "https://www.auratraveler.com/logo.png",
        "description": "AI-powered luxury travel itinerary generator for agencies, hotels, and discerning travelers.",
        "email": "concierge@auratraveler.com",
        "sameAs": [
          "https://instagram.com/auratraveler_com",
          "https://x.com/AuraTraveler",
          "https://linkedin.com/company/auratraveler",
          "https://facebook.com/auratraveler"
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Aura Traveler",
        "url": "https://www.auratraveler.com",
        "applicationCategory": "TravelApplication",
        "operatingSystem": "Web",
        "description": "AI-powered luxury travel design platform that generates magazine-quality travel itineraries in under 90 seconds.",
        "offers": {
          "@type": "AggregateOffer",
          "lowPrice": String(PRICING.dayPass.price),
          "highPrice": String(PRICING.atelier.priceMonthly),
          "priceCurrency": "USD"
        },
        "featureList": "AI-generated itineraries, White-label branding, Social studio, AI travel assistant, 140+ countries"
      }) }} />

      {/* --- HEADER / NAVIGATION --- */}
      <div className="fixed top-0 left-0 w-full z-50">
        <KobuNav variant={scrollY > 50 ? 'solid' : 'transparent'} />
      </div>

      {/* --- HERO SECTION --- */}
      <section className="relative min-h-[80dvh] md:h-[100dvh] flex flex-col items-center justify-center text-center px-4 sm:px-6 overflow-hidden pt-24 md:pt-32 pb-12 md:pb-0">
        {/* Background Slideshow — pure CSS transitions, no motion library needed */}
        <div className="absolute inset-0 z-0">
          {HERO_IMAGES.map((img, i) => (
            <div
              key={i}
              role={i === currentImageIndex ? "img" : undefined}
              aria-label={i === currentImageIndex ? "Luxury travel destination" : undefined}
              className="absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-700 ease-out"
              style={{
                backgroundImage: `url("${img}")`,
                opacity: i === currentImageIndex ? 0.85 : 0,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-parchment/40 via-parchment/30 to-parchment z-0"></div>

        <div
          className="relative z-10 max-w-6xl mx-auto hero-fade-in"
        >
            <span className="kobu-label text-obsidian mb-8 opacity-100 block">Intelligent Travel Design</span>
            <h1 className="kobu-display text-[2.25rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-obsidian leading-[1.05] tracking-tight mb-4 md:mb-6 text-reveal pb-2">
              Your Perfect <span className="italic">Trip.</span><br/>
              90 Seconds.
            </h1>
            <p className="kobu-caption text-ash-gray mb-8 md:mb-10" role="doc-subtitle">Magazine-Quality Itineraries. Verified Venues. One Click.</p>
            <p className="kobu-body text-sm md:text-base lg:text-lg text-graphite max-w-xl mx-auto leading-relaxed mb-10 md:mb-16 px-6 md:px-0">
              The average luxury trip takes 16&nbsp;hours and 38&nbsp;websites to plan.
              <br className="hidden md:block" />
              Aura does it in 90&nbsp;seconds — with real reservations, verified links, and&nbsp;logistics&nbsp;that&nbsp;work.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center">
                <Link
                    to="/day-planner"
                    className="kobu-label px-10 py-5 md:px-12 md:py-5 bg-obsidian text-parchment rounded-none hover:bg-charcoal-deep transition-all duration-700 w-[280px] sm:w-auto min-h-[56px] text-center inline-flex items-center justify-center gap-2"
                >
                    Plan My Day — Free
                    <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                    to="/explore"
                    className="kobu-caption text-obsidian hover:text-ash-gray transition-colors duration-300 w-[280px] sm:w-auto min-h-[56px] text-center inline-flex items-center justify-center gap-2"
                >
                    See Sample Itineraries <span aria-hidden="true">↗</span>
                </Link>
            </div>
            {/* Trust pills — copy is honest because primary CTA above goes to /day-planner (no auth). If routing changes, update this copy first. */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-9 justify-center items-center mt-8 text-[10px] sm:text-xs text-graphite tracking-[0.1em]">
                <span className="inline-flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-obsidian" />
                    Instant — no account needed
                </span>
                <span className="inline-flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-obsidian" />
                    No credit card required
                </span>
                <span className="inline-flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-obsidian" />
                    140+ countries
                </span>
            </div>
            <div className="mt-6 text-center">
                <Link
                    to="/create"
                    className="kobu-body italic text-base text-graphite border-b border-obsidian pb-0.5 hover:text-obsidian transition-colors"
                >
                    Planning a longer trip? Build a full multi-day itinerary →
                </Link>
                <div className="mt-2.5 kobu-label text-ash-gray">
                    Free account · Saves to your vault · 30s to start
                </div>
            </div>
        </div>

      </section>

      {/* --- VERIFIABLE STATS BAR --- */}
      {/* Removed unverifiable claims: "2,400+ Itineraries Crafted" (no source) and
          "4.8 / 5" stars (no underlying review system). Kept verifiable product facts. */}
      <section className="py-10 sm:py-14 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-t border-obsidian">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16">
            <div className="text-center">
              <p className="kobu-display text-3xl sm:text-4xl text-obsidian tracking-tight">140+</p>
              <p className="kobu-label text-ash-gray mt-1">Countries Supported</p>
            </div>
            <div className="hidden sm:block w-px h-10 bg-obsidian" />
            <div className="text-center">
              <p className="kobu-display text-3xl sm:text-4xl text-obsidian tracking-tight">Under 90s</p>
              <p className="kobu-label text-ash-gray mt-1">Generation Time</p>
            </div>
            <div className="hidden sm:block w-px h-10 bg-obsidian" />
            <div className="text-center">
              <p className="kobu-display text-3xl sm:text-4xl text-obsidian tracking-tight">Verified</p>
              <p className="kobu-label text-ash-gray mt-1">Real Venue Links</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- VALUE PROPOSITION --- */}
      {/* Removed unattributed "Beta Tester" testimonial. Restore once we have a real
          attributable customer (name + city). Until then, lead with product specifics. */}
      <section className="py-12 px-4 sm:px-8 bg-parchment border-t border-obsidian">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex flex-wrap justify-center gap-8 md:gap-14 mt-10 pt-8 border-t border-obsidian">
            <div className="text-center">
              <p className="kobu-display text-2xl text-obsidian">$0</p>
              <p className="kobu-label text-ash-gray mt-1">Free to Start</p>
            </div>
            <div className="hidden sm:block w-px h-10 bg-obsidian" />
            <div className="text-center">
              <p className="kobu-display text-2xl text-obsidian">140+</p>
              <p className="kobu-label text-ash-gray mt-1">Countries Worldwide</p>
            </div>
            <div className="hidden sm:block w-px h-10 bg-obsidian" />
            <div className="text-center">
              <p className="kobu-display text-2xl text-obsidian">Real Venues</p>
              <p className="kobu-label text-ash-gray mt-1">Verified Links & Addresses</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- ABOUT / PHILOSOPHY SECTION --- */}
      <section id="about" className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 1, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              <span className="kobu-label text-ash-gray mb-10 block">The Philosophy</span>
              <h2 className="kobu-display text-3xl sm:text-5xl md:text-7xl text-obsidian leading-[0.95] mb-8 md:mb-12 tracking-tight">
                Time. Revenue. Status. <br/>
                <span className="italic text-2xl sm:text-4xl md:text-6xl">The Asymmetric Upside.</span>
              </h2>
              <p className="kobu-body text-sm sm:text-base md:text-lg text-graphite leading-relaxed mb-10 md:mb-16 max-w-xl">
                Aura Traveler is a generative design engine for the luxury travel sector —
                synthesizing real-time destination data with editorial aesthetics to produce
                hyper-personalized, magazine-quality itineraries in seconds, not&nbsp;days.
              </p>

              <div className="grid grid-cols-2 gap-8 md:gap-16">
                <div className="border-l border-obsidian pl-4 md:pl-8">
                  <span className="block text-3xl md:text-5xl kobu-display text-obsidian mb-2 md:mb-3 tracking-tight">30s</span>
                  <span className="kobu-label text-obsidian">Per Itinerary</span>
                </div>
                <div className="border-l border-obsidian pl-4 md:pl-8">
                  <span className="block text-3xl md:text-5xl kobu-display text-obsidian mb-2 md:mb-3 tracking-tight">140+</span>
                  <span className="kobu-label text-obsidian">Countries Mapped</span>
                </div>
              </div>
              <KobuViewAll href="/philosophy" className="mt-10">
                Explore Our Philosophy
              </KobuViewAll>
            </motion.div>

            <motion.div
              initial={{ opacity: 1, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ type: "spring", stiffness: 80, damping: 25 }}
              className="relative aspect-square"
            >
              <div className="absolute inset-0 border border-obsidian rounded-full animate-spin-slow" />
              <div className="absolute inset-12 border border-obsidian rounded-full animate-reverse-spin-slow" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <span className="block text-[80px] kobu-display text-obsidian leading-none">90s</span>
                  <span className="kobu-label text-obsidian">Full Itinerary</span>
                  <span className="block kobu-label text-ash-gray mt-2">vs. 16 hours manual planning</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- METHODOLOGY / HARD LOGISTICS SECTION --- */}
      <section id="methodology" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment relative border-y border-obsidian">
        <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
                
                {/* Labor Breakdown */}
                <div className="space-y-16">
                    <div>
                        <span className="kobu-label text-ash-gray mb-8 block">The Planning Reality</span>
                        <h2 className="kobu-display text-3xl sm:text-5xl md:text-7xl text-obsidian mb-4 md:mb-6 leading-[0.95] tracking-tight">
                            Why it takes <br/>
                            <span className="italic">16 hours to plan a trip</span>
                        </h2>
                        <p className="kobu-body text-xl text-graphite italic mb-4 max-w-md">
                            Traditional travel planning is a full-time job disguised as inspiration.
                        </p>
                        <p className="kobu-label text-ash-gray mb-16">
                            Source: Priceline / Harris Poll 2024
                        </p>
                    </div>

                    <div className="space-y-12">
                        <div className="flex gap-8 group">
                            <div className="kobu-display text-3xl text-ash-gray group-hover:text-obsidian transition-all duration-500">01</div>
                            <div>
                                <h4 className="kobu-heading text-xl text-obsidian mb-3 uppercase tracking-[0.2em]">Research Complexity</h4>
                                <p className="kobu-body text-base text-graphite leading-relaxed">
                                    The average traveler visits <span className="text-obsidian font-medium">38 websites</span> before booking. We cut through the noise instantly.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-8 group">
                            <div className="kobu-display text-3xl text-ash-gray group-hover:text-obsidian transition-all duration-500">02</div>
                            <div>
                                <h4 className="kobu-heading text-xl text-obsidian mb-3 uppercase tracking-[0.2em]">Logistics & Routing</h4>
                                <p className="kobu-body text-base text-graphite leading-relaxed">
                                    Calculating travel times between restaurants, hotels, and airports while ensuring your schedule actually flows.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-8 group">
                            <div className="kobu-display text-3xl text-ash-gray group-hover:text-obsidian transition-all duration-500">03</div>
                            <div>
                                <h4 className="kobu-heading text-xl text-obsidian mb-3 uppercase tracking-[0.2em]">The Vetting Phase</h4>
                                <p className="kobu-body text-base text-graphite leading-relaxed">
                                    Reading hundreds of reviews and cross-referencing social proof to ensure the "vibe" matches the reality.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="pt-12 border-t border-obsidian">
                        <blockquote className="kobu-body text-2xl text-graphite italic leading-relaxed max-w-lg">
                            "Aura Traveler processes <span className="text-obsidian">500+ variables</span> in seconds. What takes a human brain days, our system handles with instant intelligence."
                        </blockquote>
                    </div>
                </div>

                {/* Cost of Indecision Table */}
                <div className="bg-gallery-white p-6 sm:p-10 md:p-16 border border-obsidian relative overflow-hidden">
                    <h3 className="kobu-heading text-2xl text-obsidian mb-12 uppercase tracking-[0.3em]">The Time You Save</h3>

                    {/* Mobile card layout */}
                    <div className="sm:hidden space-y-3">
                        <div className="border border-obsidian p-4 transition-colors duration-500">
                            <div className="kobu-body text-sm text-graphite uppercase tracking-widest mb-3">Market Research</div>
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <div className="kobu-label text-ash-gray mb-1">Traditional</div>
                                    <div className="kobu-body text-sm text-obsidian">270 Minutes</div>
                                </div>
                                <div className="text-ash-gray text-lg">→</div>
                                <div className="text-right">
                                    <div className="kobu-label text-ash-gray mb-1">With Aura</div>
                                    <div className="text-obsidian kobu-display text-2xl tracking-tight">0.8s</div>
                                </div>
                            </div>
                        </div>
                        <div className="border border-obsidian p-4 transition-colors duration-500">
                            <div className="kobu-body text-sm text-graphite uppercase tracking-widest mb-3">Route Optimization</div>
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <div className="kobu-label text-ash-gray mb-1">Traditional</div>
                                    <div className="kobu-body text-sm text-obsidian">180 Minutes</div>
                                </div>
                                <div className="text-ash-gray text-lg">→</div>
                                <div className="text-right">
                                    <div className="kobu-label text-ash-gray mb-1">With Aura</div>
                                    <div className="text-obsidian kobu-display text-2xl tracking-tight">0.4s</div>
                                </div>
                            </div>
                        </div>
                        <div className="border border-obsidian p-4 transition-colors duration-500">
                            <div className="kobu-body text-sm text-graphite uppercase tracking-widest mb-3">Vetting &amp; Curation</div>
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <div className="kobu-label text-ash-gray mb-1">Traditional</div>
                                    <div className="kobu-body text-sm text-obsidian">240 Minutes</div>
                                </div>
                                <div className="text-ash-gray text-lg">→</div>
                                <div className="text-right">
                                    <div className="kobu-label text-ash-gray mb-1">With Aura</div>
                                    <div className="text-obsidian kobu-display text-2xl tracking-tight">Instant</div>
                                </div>
                            </div>
                        </div>
                        <div className="border border-obsidian p-4 transition-colors duration-500">
                            <div className="kobu-body text-sm text-obsidian font-medium uppercase tracking-widest mb-3">Cognitive Load</div>
                            <div className="flex items-center justify-between gap-4">
                                <div>
                                    <div className="kobu-label text-ash-gray mb-1">Traditional</div>
                                    <div className="kobu-caption text-ash-gray">Exhausting</div>
                                </div>
                                <div className="text-ash-gray text-lg">→</div>
                                <div className="text-right">
                                    <div className="kobu-label text-ash-gray mb-1">With Aura</div>
                                    <div className="kobu-caption text-obsidian">One-Click</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop table layout */}
                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-obsidian">
                                    <th className="py-6 kobu-label text-obsidian">Task</th>
                                    <th className="py-6 kobu-label text-obsidian">Traditional</th>
                                    <th className="py-6 kobu-label text-ash-gray">With Aura</th>
                                </tr>
                            </thead>
                            <tbody className="kobu-body text-base">
                                <tr className="border-b border-obsidian group transition-colors duration-500">
                                    <td className="py-8 text-graphite">Market Research</td>
                                    <td className="py-8 text-obsidian">16 hours</td>
                                    <td className="py-8 text-obsidian kobu-display text-xl tracking-tight">Under 90 sec</td>
                                </tr>
                                <tr className="border-b border-obsidian group transition-colors duration-500">
                                    <td className="py-8 text-graphite">Route Optimization</td>
                                    <td className="py-8 text-obsidian">3 hours</td>
                                    <td className="py-8 text-obsidian kobu-display text-xl tracking-tight">Under 90 sec</td>
                                </tr>
                                <tr className="border-b border-obsidian group transition-colors duration-500">
                                    <td className="py-8 text-graphite">Vetting & Curation</td>
                                    <td className="py-8 text-obsidian">5 hours</td>
                                    <td className="py-8 text-obsidian kobu-display text-xl tracking-tight">Instant</td>
                                </tr>
                                <tr className="group transition-colors duration-500">
                                    <td className="py-8 text-obsidian font-medium">Cognitive Load</td>
                                    <td className="py-8 text-ash-gray uppercase tracking-widest kobu-caption">Exhausting</td>
                                    <td className="py-8 text-obsidian uppercase tracking-widest kobu-caption">One-Click</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-12 p-6 bg-parchment border-l-2 border-obsidian">
                        <p className="kobu-label text-ash-gray leading-relaxed normal-case">
                            <span className="text-obsidian font-medium">Sources:</span> Market research time based on Priceline/Harris Poll 2024, which found travelers spend an average of 16 hours researching a trip across 38+ websites before booking.
                        </p>
                    </div>
                </div>

            </div>
        </div>
      </section>

      {/* --- BENEFITS SECTION --- */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment relative border-t border-obsidian">
          <div className="max-w-7xl mx-auto">

              {/* Refined Header & Quote Area */}
              <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-obsidian pb-12">
                  <div className="max-w-xl">
                      <span className="kobu-label text-ash-gray mb-6 block">The Aura Advantage</span>
                      <h2 className="kobu-display text-4xl md:text-6xl text-obsidian leading-tight mb-6">
                          Time. Freedom. <span className="italic text-ash-gray">Scale.</span>
                      </h2>
                  </div>

                  <div className="flex flex-col items-end gap-6 mt-12 md:mt-0">
                       <p className="kobu-body text-2xl md:text-3xl text-graphite italic text-right leading-snug">
                           "We don't just save minutes; <br/> we manufacture <span className="text-obsidian border-b border-obsidian">prestige</span>."
                       </p>
                       <div className="flex items-center gap-4">
                           <span className="h-px w-12 bg-obsidian"></span>
                           <p className="kobu-label text-ash-gray">
                               The ROI of Aura is measured in <span className="text-obsidian px-2">Retention</span> & <span className="text-obsidian px-2">Referrals</span>
                           </p>
                       </div>
                  </div>
              </div>

              {/* Grid Layout - Three Pillars */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-obsidian border border-obsidian">

                  {/* Pillar 1: Time (Workflow Velocity) */}
                  <div className="bg-gallery-white p-8 md:p-16 relative overflow-hidden group hover:-translate-y-1 transition-all duration-500">
                      <div className="relative z-10">
                          <span className="kobu-label text-ash-gray mb-4 md:mb-6 block">01. Time</span>
                          <h3 className="kobu-heading text-2xl md:text-3xl text-obsidian mb-6 md:mb-8 tracking-tight">Workflow Velocity</h3>
                          <div className="flex items-baseline text-obsidian mb-6 md:mb-10">
                              <span className="kobu-display text-5xl md:text-7xl tracking-tight">30</span>
                              <span className="kobu-body text-xl md:text-2xl italic ml-3 text-ash-gray">sec</span>
                          </div>
                          <p className="kobu-body text-graphite text-base leading-relaxed">
                              Synthesis of logistics, live weather, and "vibe alignment" happens instantly.
                              What used to take a morning now happens between sips of espresso.
                          </p>
                      </div>
                  </div>

                  {/* Pillar 2: Revenue (Conversion & Upsell) */}
                  <div className="bg-gallery-white p-8 md:p-16 relative overflow-hidden group hover:-translate-y-1 transition-all duration-500">
                      <div className="relative z-10">
                          <span className="kobu-label text-ash-gray mb-4 md:mb-6 block">02. Revenue</span>
                          <h3 className="kobu-heading text-2xl md:text-3xl text-obsidian mb-6 md:mb-8 tracking-tight">Asymmetric Growth</h3>
                          <div className="flex items-baseline text-obsidian mb-6 md:mb-10">
                              <span className="kobu-display text-5xl md:text-7xl tracking-tight">+26</span>
                              <span className="kobu-body text-xl md:text-2xl italic ml-3 text-ash-gray">%</span>
                          </div>
                          <p className="kobu-body text-graphite text-base leading-relaxed">
                              Personalized visual proposals convert at significantly higher rates than generic itineraries.
                              The "Aura Effect" justifies premium service fees and unlocks high-margin upsells.
                              <span className="block mt-3 kobu-label text-ash-gray not-italic">Source: Epsilon Personalization Research</span>
                          </p>
                      </div>
                  </div>

                  {/* Pillar 3: Status (Brand Authority) */}
                  <div className="bg-gallery-white p-8 md:p-16 relative overflow-hidden group hover:-translate-y-1 transition-all duration-500">
                      <div className="relative z-10">
                          <span className="kobu-label text-ash-gray mb-4 md:mb-6 block">03. Status</span>
                          <h3 className="kobu-heading text-2xl md:text-3xl text-obsidian mb-6 md:mb-8 tracking-tight">Elite Positioning</h3>
                          <div className="flex items-baseline text-obsidian mb-6 md:mb-10">
                              <span className="kobu-display text-5xl md:text-7xl tracking-tight">Top</span>
                              <span className="kobu-body text-xl md:text-2xl italic ml-3 text-ash-gray">1%</span>
                          </div>
                          <p className="kobu-body text-graphite text-base leading-relaxed">
                              Magazine-quality layouts and cinematic social reels position your brand as a global authority.
                              You are no longer a vendor; you are an architect of culture.
                          </p>
                          <p className="kobu-label text-ash-gray mt-4">Personalized proposals convert 26% higher · Epsilon Research</p>
                      </div>
                  </div>
              </div>

          </div>
      </section>

      {/* --- SPECIALIZED SOLUTIONS (Hoteliers & Agents) --- */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-t border-obsidian">
          <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">

                  {/* For Hoteliers */}
                  <div className="bg-gallery-white p-8 md:p-16 border border-obsidian hover:-translate-y-1 transition-all duration-500 group">
                      <span className="kobu-label text-ash-gray mb-8 block">For Hoteliers</span>
                      <h3 className="kobu-display text-4xl text-obsidian mb-8 leading-tight tracking-tight">Elevate Guest <br/><span className="italic">Status.</span></h3>
                      <p className="kobu-body text-graphite mb-12 leading-relaxed text-lg">
                          Transform your concierge desk into a high-fidelity design studio.
                          Provide guests with bespoke, branded itineraries that extend the luxury of your property into the surrounding world.
                      </p>
                      <a
                        href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackDemoClick('home', 'book_demo_b2b_banner')}
                        className="kobu-label inline-block px-10 py-5 bg-obsidian text-parchment hover:bg-charcoal-deep transition-all duration-500"
                      >
                          Book a Demo
                      </a>
                  </div>

                  {/* For Travel Designers */}
                  <div className="bg-gallery-white p-8 md:p-16 border border-obsidian hover:-translate-y-1 transition-all duration-500 group">
                      <span className="kobu-label text-ash-gray mb-8 block">For Travel Professionals</span>
                      <h3 className="kobu-display text-4xl text-obsidian mb-8 leading-tight tracking-tight">Close More Deals. <br/><span className="italic">Effortlessly.</span></h3>
                      <p className="kobu-body text-graphite mb-12 leading-relaxed text-lg">
                          Your clients expect a luxury experience before they even leave home.
                          Aura turns your expertise into stunning, branded itineraries that close deals faster — without adding to your workload.
                      </p>
                      <a
                        href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackDemoClick('home', 'book_demo_b2b_banner')}
                        className="kobu-label inline-block px-10 py-5 bg-obsidian text-parchment hover:bg-charcoal-deep transition-all duration-500"
                      >
                          Book a Demo
                      </a>
                  </div>

              </div>
          </div>
      </section>

      {/* --- JOURNAL SECTION (Carousel Style) --- */}
      <section id="journal" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-t border-obsidian relative z-10">
        <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-12 md:mb-20 gap-4">
                <div>
                    <span className="kobu-label text-ash-gray mb-6 block">The Journal</span>
                    <h2 className="kobu-display text-3xl sm:text-5xl md:text-7xl text-obsidian tracking-tight">Curated Expeditions</h2>
                </div>
                <KobuViewAll href="/journal">View Journal</KobuViewAll>
            </div>

            {/* Carousel Content */}
            <div className="relative">
                <div className="flex flex-col md:flex-row items-center gap-16 lg:gap-24">
                    
                    {/* Active Image */}
                    <motion.div
                      className="w-full md:w-1/2 relative group cursor-pointer"
                      onClick={() => { trackSampleView(activeJournal.title); setSelectedSample(activeJournal); }}
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <div className="aspect-[4/5] bg-gallery-white rounded-none overflow-hidden border border-obsidian relative">
                             <motion.img
                                key={activeJournal.image}
                                src={activeJournal.image}
                                srcSet={`${activeJournal.image.replace(/w=\d+/, 'w=400')} 400w, ${activeJournal.image.replace(/w=\d+/, 'w=800')} 800w, ${activeJournal.image.replace(/w=\d+/, 'w=1200')} 1200w`}
                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                alt={activeJournal.title}
                                loading="lazy"
                                decoding="async"
                                initial={{ opacity: 0, scale: 1.05 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    // Robust Fallback
                                    e.currentTarget.src = "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=800&q=75&fm=webp&auto=format&fit=crop";
                                }}
                             />
                             <div className="absolute inset-0 bg-gradient-to-t from-parchment/40 to-transparent opacity-60"></div>

                             {/* Number Watermark */}
                             <div className="absolute top-8 left-8 kobu-display text-[120px] text-parchment/40 pointer-events-none select-none tracking-tight">
                                 0{journalIndex + 1}
                             </div>
                        </div>
                    </motion.div>

                    {/* Active Details */}
                    <motion.div
                      className="w-full md:w-1/2 text-left"
                      key={activeJournal.id}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <div className="flex items-center gap-6 mb-8">
                            <span className="w-12 h-px bg-obsidian"></span>
                            <span className="kobu-label text-obsidian">
                                {activeJournal.location}
                            </span>
                        </div>

                        <h3 className="kobu-display text-3xl sm:text-5xl md:text-7xl text-obsidian mb-6 md:mb-10 leading-[0.9] tracking-tight">
                            {activeJournal.title}
                        </h3>

                        <p className="kobu-body text-xl text-graphite leading-relaxed mb-12 max-w-md border-l border-obsidian pl-8 italic">
                            "{activeJournal.description}"
                        </p>

                        <div className="flex items-center gap-10 mb-16">
                             <button
                                onClick={() => { trackSampleView(activeJournal.title); setSelectedSample(activeJournal); }}
                                className="kobu-label px-10 py-4 border border-obsidian text-obsidian hover:bg-obsidian hover:text-parchment transition-all duration-500"
                             >
                                 Explore Itinerary
                             </button>
                             <div className="flex gap-4">
                                {activeJournal.tags.map(tag => (
                                    <span key={tag} className="kobu-label text-ash-gray">#{tag}</span>
                                ))}
                             </div>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-8">
                            <button
                                onClick={prevJournal}
                                aria-label="Previous journal entry"
                                className="w-14 h-14 rounded-full border border-obsidian flex items-center justify-center text-obsidian hover:bg-obsidian hover:text-parchment transition-all duration-500"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="font-fira text-xs text-obsidian tracking-[0.3em]">
                                0{journalIndex + 1} / 0{JOURNAL_ENTRIES.length}
                            </span>
                            <button
                                onClick={nextJournal}
                                aria-label="Next journal entry"
                                className="w-14 h-14 rounded-full border border-obsidian flex items-center justify-center text-obsidian hover:bg-obsidian hover:text-parchment transition-all duration-500"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </motion.div>

                </div>
            </div>

        </div>
      </section>

      {/* --- FEATURE GRID --- */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-t border-obsidian">
          <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 md:mb-24">
                  <span className="kobu-label text-ash-gray mb-8 block">The Toolkit</span>
                  <h2 className="kobu-display text-3xl sm:text-5xl md:text-7xl text-obsidian tracking-tight">Engineered for the <span className="italic">Elite</span></h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-obsidian border border-obsidian">
                  {/* Card 1 */}
                  <motion.div
                      initial={{ opacity: 1, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ type: "spring", stiffness: 150, damping: 25, delay: 0 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-gallery-white p-6 sm:p-8 md:p-16 transition-all duration-700 group relative overflow-hidden hover:-translate-y-1 rounded-none"
                  >
                      <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity duration-700">
                          <span className="kobu-display text-[140px] text-obsidian leading-none">01</span>
                      </div>
                      <div className="w-14 h-14 border border-obsidian rounded-full flex items-center justify-center mb-10 text-obsidian transition-all duration-300">
                          <Shield className="w-6 h-6" />
                      </div>
                      <h3 className="kobu-heading text-2xl mb-6 text-obsidian tracking-tight">Bespoke Itineraries</h3>
                      <p className="kobu-body text-base text-graphite leading-relaxed">
                          Produce white-labeled, high-fidelity digital itineraries complete with logistics, packing lists, and "concierge notes" tailored to your client's specific vibe.
                      </p>
                  </motion.div>

                  {/* Card 2 */}
                  <motion.div
                      initial={{ opacity: 1, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ type: "spring", stiffness: 150, damping: 25, delay: 0.1 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-gallery-white p-6 sm:p-8 md:p-16 transition-all duration-700 group relative overflow-hidden hover:-translate-y-1 rounded-none"
                  >
                      <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity duration-700">
                          <span className="kobu-display text-[140px] text-obsidian leading-none">02</span>
                      </div>
                      <div className="w-14 h-14 border border-obsidian rounded-full flex items-center justify-center mb-10 text-obsidian transition-all duration-300">
                          <Zap className="w-6 h-6" />
                      </div>
                      <h3 className="kobu-heading text-2xl mb-6 text-obsidian tracking-tight">Aura Social Studio</h3>
                      <p className="kobu-body text-base text-graphite leading-relaxed">
                          The Aura Engine doesn't just write text; it directs video. Generate 9:16 vertical reels with cinematic pan-and-zoom motion to showcase destinations on Instagram.
                      </p>
                  </motion.div>

                  {/* Card 3 */}
                  <motion.div
                      initial={{ opacity: 1, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ type: "spring", stiffness: 150, damping: 25, delay: 0.2 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-gallery-white p-6 sm:p-8 md:p-16 transition-all duration-700 group relative overflow-hidden hover:-translate-y-1 rounded-none"
                  >
                      <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity duration-700">
                          <span className="kobu-display text-[140px] text-obsidian leading-none">03</span>
                      </div>
                      <div className="w-14 h-14 border border-obsidian rounded-full flex items-center justify-center mb-10 text-obsidian transition-all duration-300">
                          <Globe className="w-6 h-6" />
                      </div>
                      <h3 className="kobu-heading text-2xl mb-6 text-obsidian tracking-tight">AI Travel Assistant</h3>
                      <p className="kobu-body text-base text-graphite leading-relaxed">
                          Ask Aura about your trip — restaurant swaps, hidden gems, logistics questions. Contextual answers anchored to your itinerary.
                      </p>
                  </motion.div>
              </div>
          </div>
      </section>

      {/* --- AURA vs. GENERIC AI COMPARISON --- */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-b border-obsidian relative overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
              <div className="text-center mb-16 md:mb-20">
                  <span className="kobu-label text-ash-gray mb-6 block">The Difference</span>
                  <h2 className="kobu-display text-4xl md:text-5xl text-obsidian tracking-tight">Why Not Just Use <span className="italic text-ash-gray">ChatGPT?</span></h2>
                  <p className="kobu-body text-base text-graphite italic mt-6 max-w-xl mx-auto">Generic AI gives you a wall of text. Aura gives you a travel document you'd actually use.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                  {/* Generic AI Column */}
                  <div className="p-8 md:p-10 border border-obsidian bg-gallery-white relative">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-ash-gray" />
                      <h3 className="kobu-heading text-xl uppercase tracking-[0.2em] text-ash-gray mb-8">ChatGPT / Perplexity</h3>
                      <div className="space-y-5">
                          <div className="flex items-start gap-3">
                              <span className="text-ash-gray mt-0.5">✕</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Plain text list of suggestions — no structure, no design</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-ash-gray mt-0.5">✕</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Hallucinated restaurant names and outdated hotel info</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-ash-gray mt-0.5">✕</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">No maps, no images, no booking links</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-ash-gray mt-0.5">✕</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Can't share, print, or brand the output</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-ash-gray mt-0.5">✕</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Requires 10+ follow-up prompts to get a usable plan</p>
                          </div>
                      </div>
                  </div>

                  {/* Aura Column */}
                  <div className="p-8 md:p-10 border border-obsidian bg-gallery-white relative">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-obsidian" />
                      <h3 className="kobu-heading text-xl uppercase tracking-[0.2em] text-obsidian mb-8">Aura Traveler</h3>
                      <div className="space-y-5">
                          <div className="flex items-start gap-3">
                              <span className="text-obsidian mt-0.5">✓</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Magazine-quality layout with day-by-day structure and imagery</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-obsidian mt-0.5">✓</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Real venues with verified addresses and booking links</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-obsidian mt-0.5">✓</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Interactive maps, curated photography, and logistics built in</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-obsidian mt-0.5">✓</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">One-click sharing, email dispatch, and social media story cards</p>
                          </div>
                          <div className="flex items-start gap-3">
                              <span className="text-obsidian mt-0.5">✓</span>
                              <p className="kobu-body text-graphite text-sm leading-relaxed">Complete itinerary in one click — no prompt engineering needed</p>
                          </div>
                      </div>
                  </div>
              </div>

              <div className="text-center mt-12">
                  <Link
                      to="/day-planner"
                      className="kobu-label px-10 py-5 bg-obsidian text-parchment hover:bg-charcoal-deep transition-all duration-500 rounded-none inline-flex items-center"
                  >
                      See the Difference — Try Free
                  </Link>
              </div>
          </div>
      </section>

       {/* --- PRICING / MEMBERSHIP --- */}
       <section id="pricing" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-y border-obsidian">
          <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                  <span className="kobu-label text-ash-gray mb-4 block">Membership</span>
                  <div className="inline-block px-4 py-1.5 border border-obsidian rounded-[5px] mb-6">
                      <span className="kobu-label text-obsidian">Free to Start — No Card Required</span>
                  </div>
                  <h2 className="kobu-display text-5xl md:text-7xl text-obsidian tracking-tight">Access Tiers</h2>
              </div>
              <p className="text-center kobu-body text-base text-graphite italic mb-4 max-w-xl mx-auto">{COPY.homeAnchor}</p>
              <p className="text-center kobu-label text-ash-gray mb-12">{COPY.homeBanner}</p>

              {/* Plan My Day CTA — free, no auth */}
              <div className="max-w-2xl mx-auto mb-14">
                <Link to="/day-planner" className="block w-full p-6 sm:p-8 border border-obsidian hover:bg-gallery-white transition-all duration-500 bg-gallery-white group text-center">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                    <div>
                      <span className="text-obsidian kobu-display text-3xl tracking-tight">Free</span>
                      <span className="text-ash-gray text-xs ml-2 uppercase tracking-widest">to start</span>
                    </div>
                    <div className="h-8 w-px bg-obsidian hidden sm:block" />
                    <div className="text-center sm:text-left">
                      <h4 className="text-obsidian kobu-heading text-xl uppercase tracking-wider mb-1">Plan My Day</h4>
                      <p className="text-[11px] text-graphite kobu-body italic">AI-powered luxury itineraries • Magazine-quality visuals • Instant generation</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-obsidian group-hover:translate-x-1 transition-all shrink-0" />
                  </div>
                </Link>
              </div>

              <div className="flex justify-center mb-10">
                <Link
                  to="/pricing"
                  className="kobu-label text-ash-gray hover:text-obsidian transition-colors duration-300"
                >
                  View All Plans &amp; Pricing &rarr;
                </Link>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 max-w-5xl mx-auto items-stretch">
                  {/* Individual Traveler Box */}
                  <div className="bg-gallery-white border border-obsidian p-6 sm:p-8 md:p-12 flex flex-col transition-all duration-700 group">
                      <div className="text-center mb-12">
                          <h3 className="kobu-heading text-3xl text-obsidian mb-4 uppercase tracking-[0.3em]">Individual</h3>
                          <div className="h-px w-16 bg-obsidian mx-auto mb-6"></div>
                          <p className="kobu-label text-ash-gray">Single Traveler Itineraries</p>
                      </div>

                      <div className="space-y-6 mb-16 flex-grow">
                          {/* Free Day Planner */}
                          <div className="p-8 border border-obsidian transition-all duration-500 cursor-pointer bg-parchment group/item">
                              <div className="flex justify-between items-center mb-3">
                                  <span className="text-obsidian kobu-heading text-xl uppercase tracking-wider transition-colors">Day Planner</span>
                                  <span className="text-obsidian kobu-display text-2xl tracking-tight">Free</span>
                              </div>
                              <p className="text-[11px] text-graphite kobu-body italic">Single-day AI itinerary • Real venues • No signup required</p>
                          </div>

                          {/* Day Pass */}
                          <div className="p-8 border border-obsidian transition-all duration-500 cursor-pointer bg-parchment group/item">
                              <div className="flex justify-between items-center mb-3">
                                  <span className="text-obsidian kobu-heading text-xl uppercase tracking-wider transition-colors">Day Pass</span>
                                  <span className="text-obsidian kobu-display text-2xl tracking-tight">{fmt(PRICING.dayPass.price)}</span>
                              </div>
                              <p className="text-[11px] text-graphite kobu-body italic">One premium magazine-quality multi-day itinerary • Saved to your vault • Shareable</p>
                          </div>

                          {/* Pro — most popular */}
                          <div className="p-8 border border-obsidian transition-all duration-500 cursor-pointer bg-parchment group/item relative overflow-hidden">
                              <div className="absolute top-0 right-0 bg-obsidian text-parchment text-[10px] px-3 py-1 uppercase font-bold tracking-widest">Most Popular</div>
                              <div className="flex justify-between items-center mb-3">
                                  <span className="text-obsidian kobu-heading text-xl uppercase tracking-wider transition-colors">Pro</span>
                                  <span className="text-obsidian kobu-display text-2xl tracking-tight">{fmt(PRICING.pro.priceYearly)}<span className="text-sm text-ash-gray font-fira">/yr</span></span>
                              </div>
                              <p className="text-[11px] text-graphite kobu-body italic">{COPY.proCardLine} • Perpetual vault • Priority generation</p>
                              <span className="inline-block mt-3 kobu-label text-ash-gray border border-obsidian px-2 py-0.5 rounded-[5px]">{COPY.proChip}</span>
                          </div>
                      </div>

                      <div className="space-y-3">
                          <Link to="/day-planner" className="block w-full py-5 border border-obsidian text-obsidian kobu-label hover:bg-obsidian hover:text-parchment transition-all duration-700 min-h-[48px] text-center">Create My Itinerary — Free to Start</Link>
                          <p className="text-center text-ash-gray kobu-label">Pick a destination → Choose your style → Get your itinerary in 90 seconds</p>
                      </div>
                  </div>

                  {/* Agency & Enterprise Box */}
                  <div className="bg-gallery-white border border-obsidian p-6 sm:p-8 md:p-12 flex flex-col relative lg:scale-105 z-10">
                      <div className="absolute top-0 inset-x-0 h-1 bg-obsidian"></div>

                      <div className="text-center mb-12">
                          <h3 className="kobu-heading text-3xl text-obsidian mb-4 uppercase tracking-[0.3em]">Agency</h3>
                          <div className="h-px w-16 bg-obsidian mx-auto mb-6"></div>
                          <p className="kobu-label text-ash-gray">Professional Enterprise Solutions</p>
                      </div>

                      <div className="space-y-6 mb-16 flex-grow">
                          {/* White-Label Agency */}
                          <div className="p-8 border border-obsidian transition-all duration-500 cursor-pointer bg-parchment group/item">
                              <div className="flex justify-between items-center mb-3">
                                  <span className="text-obsidian kobu-heading text-xl uppercase tracking-wider transition-colors">{PRICING.atelier.name}</span>
                                  <span className="text-obsidian kobu-display text-xl tracking-tight">{fmt(PRICING.atelier.priceMonthly)}<span className="text-sm text-ash-gray font-fira">/mo</span></span>
                              </div>
                              <p className="text-[11px] text-graphite kobu-body italic">{PRICING.atelier.itinerariesPerMonth} White-Label Itineraries/mo • Custom Branding • Team Collaboration • Client Portal</p>
                          </div>

                          {/* Enterprise */}
                          <div className="p-8 border border-obsidian transition-all duration-500 cursor-pointer bg-parchment group/item">
                              <div className="flex justify-between items-center mb-3">
                                  <div className="flex items-center gap-3">
                                      <span className="text-obsidian kobu-heading text-xl uppercase tracking-wider transition-colors">Enterprise</span>
                                      <span className="text-[10px] bg-obsidian text-parchment px-2 py-0.5 uppercase font-bold tracking-widest">Scale</span>
                                  </div>
                                  <span className="text-obsidian kobu-display text-xl tracking-tight">from {fmt(PRICING.enterprise.fromMonthly)}<span className="text-sm text-ash-gray font-fira">/mo</span></span>
                              </div>
                              <p className="text-[11px] text-graphite kobu-body italic">Custom Integrations • Multi-User Teams • API Access • Dedicated Support</p>
                          </div>
                      </div>

                      <a href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer" onClick={() => trackDemoClick('home', 'book_demo_enterprise')} className="block w-full py-5 bg-obsidian text-parchment kobu-label hover:bg-charcoal-deep transition-all duration-700 min-h-[48px] text-center">Book a Demo</a>
                  </div>
              </div>

              {/* Founding Partner strip (doc A3) — public seat counter drives urgency */}
              <div id="founding-partner" className="max-w-5xl mx-auto mt-10 border border-obsidian bg-parchment p-8 md:p-10 text-center">
                  <h3 className="kobu-heading text-xl md:text-2xl text-obsidian uppercase tracking-[0.2em] mb-4">{COPY.foundingStripHeadline}</h3>
                  <p className="kobu-body text-[15px] text-graphite italic max-w-2xl mx-auto mb-6">{COPY.foundingStripBody}</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <span className="kobu-label text-obsidian border border-obsidian px-3 py-1">{foundingSeatsLabel()}</span>
                      <a href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer" onClick={() => trackDemoClick('home', 'claim_founding_seat')} className="kobu-label px-8 py-4 bg-obsidian text-parchment hover:bg-charcoal-deep transition-all duration-500">{COPY.foundingStripCta}</a>
                  </div>
              </div>
          </div>
       </section>

      {/* --- AURA STUDIO: SOCIAL SHARING & REFERRALS --- */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-b border-obsidian relative overflow-hidden">
          <div className="max-w-6xl mx-auto relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
                  {/* Left: Content */}
                  <div>
                      <span className="kobu-label text-ash-gray mb-6 block">Aura Studio</span>
                      <h2 className="kobu-display text-4xl md:text-5xl text-obsidian tracking-tight mb-6">Share Your <span className="italic">Story</span></h2>
                      <p className="kobu-body text-graphite text-base leading-relaxed mb-8">
                          Every itinerary is a work of art — and art is meant to be shared. Aura Studio transforms your travel plans into stunning social content that inspires wanderlust and drives referrals.
                      </p>

                      <div className="space-y-6 mb-10">
                          <div className="flex items-start gap-4">
                              <div className="w-10 h-10 border border-obsidian rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <svg className="w-4 h-4 text-obsidian" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              </div>
                              <div>
                                  <p className="kobu-caption text-obsidian mb-1">Story Cards & Social Posts</p>
                                  <p className="kobu-body text-graphite text-sm leading-relaxed">Generate Instagram Stories, feed posts, and Twitter cards from any itinerary — perfectly sized, beautifully branded.</p>
                              </div>
                          </div>

                          <div className="flex items-start gap-4">
                              <div className="w-10 h-10 border border-obsidian rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <svg className="w-4 h-4 text-obsidian" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              </div>
                              <div>
                                  <p className="kobu-caption text-obsidian mb-1">AI Video Reels</p>
                                  <p className="kobu-body text-graphite text-sm leading-relaxed">Turn any destination into a cinematic 6-second travel reel — ready for TikTok, Reels, or your agency's portfolio.</p>
                              </div>
                          </div>

                          <div className="flex items-start gap-4">
                              <div className="w-10 h-10 border border-obsidian rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <svg className="w-4 h-4 text-obsidian" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                              </div>
                              <div>
                                  <p className="kobu-caption text-obsidian mb-1">Referral Power</p>
                                  <p className="kobu-body text-graphite text-sm leading-relaxed">Shared itineraries become your best marketing. When clients share their travel plans, their network sees your brand — not a generic itinerary.</p>
                              </div>
                          </div>
                      </div>

                      <div className="flex flex-wrap gap-4">
                          <Link
                              to="/create"
                              className="kobu-label px-6 py-3 bg-obsidian text-parchment rounded-none hover:bg-charcoal-deep transition-all duration-500 inline-flex items-center"
                          >
                              Try Aura Studio
                          </Link>
                          <Link
                              to="/pricing"
                              className="kobu-label px-6 py-3 border border-obsidian text-obsidian rounded-none hover:bg-obsidian hover:text-parchment transition-all duration-500"
                          >
                              View Plans
                          </Link>
                      </div>
                  </div>

                  {/* Right: Visual */}
                  <div className="relative">
                      <div className="relative rounded-none overflow-hidden aspect-[4/5]">
                          <img
                              src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=75&fm=webp&auto=format&fit=crop"
                              srcSet="https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=400&q=75&fm=webp&auto=format&fit=crop 400w, https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=75&fm=webp&auto=format&fit=crop 800w, https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1200&q=75&fm=webp&auto=format&fit=crop 1200w"
                              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                              alt="Travel content creation"
                              loading="lazy"
                              decoding="async"
                              className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 via-transparent to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-8">
                              <div className="flex items-center gap-3 mb-4">
                                  <div className="w-8 h-8 rounded-full bg-gallery-white flex items-center justify-center">
                                      <svg className="w-3.5 h-3.5 text-obsidian" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" /></svg>
                                  </div>
                                  <div>
                                      <p className="text-parchment text-[11px] font-semibold uppercase tracking-wider">Story Card</p>
                                      <p className="text-parchment/70 text-[10px]">Ready to share</p>
                                  </div>
                              </div>
                              <div className="flex gap-2">
                                  <span className="px-3 py-1 bg-gallery-white text-obsidian text-[10px] font-bold uppercase tracking-wider rounded-none">Instagram</span>
                                  <span className="px-3 py-1 bg-gallery-white text-obsidian text-[10px] font-bold uppercase tracking-wider rounded-none">TikTok</span>
                                  <span className="px-3 py-1 bg-gallery-white text-obsidian text-[10px] font-bold uppercase tracking-wider rounded-none">Twitter</span>
                              </div>
                          </div>
                      </div>

                      {/* Floating stat card */}
                      <div className="absolute -top-4 -right-4 bg-gallery-white border border-obsidian rounded-none p-5">
                          <p className="kobu-display text-2xl text-obsidian tracking-tight">1-Click</p>
                          <p className="kobu-label text-ash-gray mt-1">Social-ready content</p>
                          <p className="kobu-label text-ash-gray mt-0.5">Stories, Reels & Cards</p>
                      </div>
                  </div>
              </div>
          </div>
      </section>

      {/* --- NEWSLETTER / WAITLIST --- */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-b border-obsidian relative overflow-hidden">
          <div className="max-w-2xl mx-auto text-center relative z-10">
              <div className="w-14 h-14 border border-obsidian rounded-full flex items-center justify-center mx-auto mb-10 text-obsidian">
                  <Mail className="w-6 h-6" />
              </div>
              <span className="kobu-label text-ash-gray mb-6 block">Stay Connected</span>
              <h2 className="kobu-display text-4xl md:text-5xl text-obsidian tracking-tight mb-6">The Dispatch</h2>
              <p className="kobu-body text-base text-graphite leading-relaxed mb-12 max-w-lg mx-auto italic">
                  Curated destination intelligence, early access to new features, and insider travel insights — delivered to your inbox.
              </p>

              {dispatchStatus === 'success' ? (
                  <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="py-5 px-8 border border-obsidian bg-gallery-white inline-block"
                  >
                      <p className="kobu-caption text-obsidian">
                          {dispatchMessage === 'already_subscribed' ? 'You\'re already on the list.' : 'Welcome to The Dispatch.'}
                      </p>
                  </motion.div>
              ) : (
                  <form onSubmit={handleDispatchSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                      <input
                          type="email"
                          name="email"
                          aria-label="Email address for The Dispatch newsletter"
                          value={dispatchEmail}
                          onChange={(e) => setDispatchEmail(e.target.value)}
                          placeholder="your@email.com"
                          required
                          className="flex-1 px-6 py-4 bg-gallery-white border border-obsidian text-obsidian font-gill text-sm placeholder:text-ash-gray focus:outline-none transition-colors duration-300"
                      />
                      <button
                          type="submit"
                          disabled={dispatchStatus === 'sending'}
                          className="px-8 py-4 bg-obsidian text-parchment kobu-label hover:bg-charcoal-deep transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-h-[48px]"
                      >
                          {dispatchStatus === 'sending' ? 'Subscribing...' : 'Subscribe'}
                      </button>
                  </form>
              )}

              {dispatchStatus === 'error' && (
                  <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="mt-4 kobu-caption text-obsidian tracking-wide"
                  >
                      {dispatchMessage || 'Something went wrong. Please try again.'}
                  </motion.p>
              )}

              <p className="mt-8 kobu-label text-ash-gray">No spam. Unsubscribe anytime.</p>
          </div>
      </section>

      {/* --- FAQ SECTION --- */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-b border-obsidian relative">
          <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16 md:mb-20">
                  <span className="kobu-label text-ash-gray mb-6 block">Knowledge</span>
                  <h2 className="kobu-display text-4xl md:text-5xl text-obsidian tracking-tight">Questions <span className="text-obsidian italic">&</span> Answers</h2>
              </div>

              <div className="space-y-0">
                  {[
                    {
                      q: "What is the best AI travel itinerary generator?",
                      a: "Aura Traveler is an AI travel itinerary generator built specifically for luxury travelers. Our engine analyzes over 500 variables — from seasonality and local events to personal preferences and insider knowledge — to produce magazine-quality itineraries covering 140+ countries in under 90 seconds. Every booking link is verified with a real HTTP check, and every venue is confirmed still operating against live web search before it reaches your itinerary."
                    },
                    {
                      q: "How does Aura Traveler create luxury itineraries?",
                      a: "Our proprietary AI engine processes over 500 variables including your travel style, dietary preferences, cultural interests, pace of travel, accommodation standards, and real-time destination intelligence. It then synthesizes this data into a beautifully designed itinerary with day-by-day itineraries, curated dining recommendations, hotel selections, and cultural experiences — all tailored specifically to you."
                    },
                    {
                      q: "How long does it take to plan a luxury trip with Aura Traveler?",
                      a: "A complete luxury travel itinerary is generated in under 90 seconds. Traditional luxury travel planning with a human concierge typically takes days or even weeks of back-and-forth. Aura Traveler delivers the same caliber of bespoke, insider-level itinerary almost instantly, allowing you to iterate and refine until every detail is perfect."
                    },
                    {
                      q: "What's included in an Aura Traveler itinerary?",
                      a: "Each itinerary is a comprehensive travel document that includes a personalized day-by-day itinerary, curated hotel and accommodation recommendations, restaurant and dining reservations, cultural experiences and local insights, interactive maps, practical travel logistics, and a beautifully designed shareable document. Think of it as your personal travel magazine, crafted exclusively for your journey."
                    },
                    {
                      q: "Can AI create truly bespoke travel itineraries?",
                      a: "Yes. Aura Traveler's AI engine produces a unique itinerary for every traveler — by processing 500+ variables and drawing on deep destination expertise, our platform consistently delivers itineraries that rival, and often surpass, those from top human travel concierges. The key difference is speed and scalability without sacrificing quality or personalization."
                    },
                    {
                      q: "Is Aura Traveler suitable for travel agencies?",
                      a: `Yes. Aura Traveler is purpose-built for travel agencies and advisors. The platform enables agencies to generate professional-quality, white-labeled itineraries for their clients in seconds rather than hours, dramatically improving efficiency while maintaining the bespoke quality their clientele expects. The Atelier plan (${fmt(PRICING.atelier.priceMonthly)}/month) includes custom branding, ${PRICING.atelier.itinerariesPerMonth} itineraries per month, and team collaboration tools.`
                    },
                    {
                      q: "How is Aura Traveler different from ChatGPT or Perplexity for travel?",
                      a: "Unlike general-purpose AI tools that produce plain text responses, Aura Traveler is purpose-built for luxury travel planning. It produces beautifully designed, shareable itineraries with interactive maps, curated high-resolution imagery, and insider-level detail. The output is a polished travel document — not a chat conversation — designed to be shared, printed, or used as a living travel companion."
                    }
                  ].map((faq, i) => (
                    <div key={i} className="border-b border-obsidian">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        aria-expanded={openFaq === i}
                        className="w-full flex justify-between items-center py-6 md:py-8 text-left group"
                      >
                        <span className="kobu-heading text-base md:text-lg text-obsidian transition-colors duration-300 pr-8 leading-relaxed">{faq.q}</span>
                        <span className={`text-obsidian text-2xl font-light transition-transform duration-300 flex-shrink-0 ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
                      </button>
                      <AnimatePresence>
                        {openFaq === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <p className="kobu-body text-sm md:text-base text-graphite leading-relaxed pb-8 pr-12">{faq.a}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
              </div>
          </div>
      </section>

      {/* --- FOOTER CTA --- */}
      <section className="py-24 sm:py-32 md:py-48 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment text-center relative overflow-hidden border-t border-obsidian">
          <h2 className="kobu-display text-4xl sm:text-6xl md:text-8xl text-obsidian mb-6 md:mb-8 tracking-tight relative z-10">Stop Researching.<br/><span className="text-obsidian italic">Start Traveling.</span></h2>
          <p className="kobu-body text-base md:text-lg text-graphite italic mb-10 md:mb-16 relative z-10 max-w-md mx-auto">Your next extraordinary journey begins with a single question. Aura designs the rest.</p>
          <div className="flex flex-col items-center gap-3 relative z-10">
              <Link
                  to="/day-planner"
                  className="kobu-label px-16 py-6 bg-obsidian text-parchment hover:bg-charcoal-deep transition-all duration-700 rounded-none inline-flex items-center"
              >
                  Design Your Itinerary
              </Link>
              <span className="text-ash-gray text-[10px] kobu-body tracking-wide">No credit card required • Takes 90 seconds</span>
              <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-ash-gray kobu-label">
                <Link to="/pricing" title={COPY.guarantee} className="hover:text-obsidian transition-colors underline decoration-dotted underline-offset-4">{COPY.guaranteeShort}</Link>
                <span className="hidden sm:inline">•</span>
                <span>Every Booking Link Verified</span>
                <span className="hidden sm:inline">•</span>
                <span>Your Data Stays Private</span>
                <span className="hidden sm:inline">•</span>
                <span>Cancel Anytime</span>
              </div>
          </div>
          <div className="mt-20 flex justify-center gap-12 relative z-10">
              <span className="kobu-label text-ash-gray">140+ Destinations</span>
              <span className="kobu-label text-ash-gray">Magazine-Quality Output</span>
              <a href="#founding-partner" className="kobu-label text-ash-gray hover:text-obsidian transition-colors">Founding Partners — {foundingSeatsLabel()}</a>
          </div>
      </section>

      {/* Sample Itinerary Modal */}
      <SampleItineraryModal
          isOpen={!!selectedSample}
          onClose={() => setSelectedSample(null)}
          voyage={selectedSample}
      />

      {/* --- EMAIL CAPTURE SECTION --- */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 md:px-8 lg:px-16 bg-parchment border-t border-obsidian">
        <div className="max-w-2xl mx-auto text-center">
          <span className="kobu-label text-ash-gray mb-6 block">Stay Inspired</span>
          <h2 className="kobu-display text-3xl sm:text-4xl md:text-5xl text-obsidian tracking-tight mb-4">
            Get a free sample itinerary
          </h2>
          <p className="kobu-body italic text-graphite text-base mb-10 max-w-lg mx-auto">
            Enter your email and dream destination. We'll send you a curated day-by-day preview — no sign-up required.
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const emailInput = form.querySelector('input[type="email"]') as HTMLInputElement;
              const destInput = form.querySelector('input[type="text"]') as HTMLInputElement;
              const btn = form.querySelector('button') as HTMLButtonElement;
              if (!emailInput?.value?.includes('@')) return;
              btn.textContent = 'Sending...';
              btn.disabled = true;
              try {
                const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
                const { db } = await import('../services/firebase');
                if (db) {
                  await addDoc(collection(db, 'email_captures'), {
                    email: emailInput.value.toLowerCase().trim(),
                    destination: destInput?.value || '',
                    source: 'homepage_lead_magnet',
                    capturedAt: serverTimestamp(),
                  });
                }
                trackEmailCapture('lead_magnet', destInput?.value || undefined);
                btn.textContent = '✓ Check Your Inbox';
                emailInput.value = '';
                if (destInput) destInput.value = '';
              } catch {
                btn.textContent = 'Try Again';
                btn.disabled = false;
              }
            }}
            className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
          >
            <input
              type="email"
              placeholder="your@email.com"
              required
              aria-label="Email address for sample itinerary"
              className="flex-1 bg-gallery-white border border-obsidian rounded-none px-5 py-4 text-obsidian text-sm font-gill placeholder:text-ash-gray focus:outline-none min-h-[48px]"
            />
            <input
              type="text"
              placeholder="Dream destination"
              aria-label="Your dream destination"
              className="flex-1 bg-gallery-white border border-obsidian rounded-none px-5 py-4 text-obsidian text-sm font-gill placeholder:text-ash-gray focus:outline-none min-h-[48px]"
            />
            <button
              type="submit"
              className="px-8 py-4 bg-obsidian text-parchment kobu-label rounded-none hover:bg-charcoal-deep transition-all min-h-[48px] shrink-0"
            >
              Send Free Preview
            </button>
          </form>
          <p className="kobu-label text-ash-gray mt-4">No spam. Unsubscribe anytime.</p>
        </div>
      </section>

      <KobuFooter />
      <ScrollToTop />
      <KobuBackToTop />

      {/* Sticky bottom CTA bar — always visible on mobile. Mirrors the hero pair. */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden safe-area-bottom">
        <div className="bg-gallery-white border-t border-obsidian px-4 py-3 flex items-center justify-between gap-3">
          <Link
            to="/day-planner"
            className="kobu-label px-6 py-2.5 bg-obsidian text-parchment rounded-none min-h-[44px] inline-flex items-center"
          >
            Plan My Day
          </Link>
          <Link
            to="/explore"
            className="kobu-caption text-obsidian min-h-[44px] flex items-center"
          >
            Samples →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
